import { useState } from 'react';
import { Link, useParams } from 'react-router';
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  ErrorState,
  Heading,
  Spinner,
  Stack,
  Text,
  TextField,
} from '../../ui';
import { formatLabel, scoringMethodLabel, statusLabel, statusTone } from '../../racing/format';
import { useRaceEvent } from '../../racing/queries';
import {
  useCancelRaceEvent,
  useCompleteRaceEvent,
  useGenerateBracket,
  useOpenRegistration,
  useRecordResult,
  useRegisterContestant,
  useWithdrawContestant,
} from '../../racing/mutations';
import type { Contestant, RaceEventDetail } from '../../racing/types';
import { BracketView } from './BracketView';
import { StandingsTable } from './StandingsTable';

/**
 * The race event hub: information, contestants, the bracket or standings, and — for the organiser —
 * lifecycle controls and live scoring.
 *
 * @returns The page element.
 */
export function RaceEventDetailPage(): React.JSX.Element {
  const { raceEventId = '' } = useParams<{ raceEventId: string }>();
  const { data, isPending, isError, error, refetch } = useRaceEvent(raceEventId);
  const record = useRecordResult(raceEventId);

  if (isPending) {
    return <Spinner label="Loading the race event" />;
  }

  if (isError) {
    return <ErrorState message={error.message} onRetry={() => void refetch()} />;
  }

  const onRecord = (matchId: string | null, contestantId: string, elapsedMs: number): void => {
    record.mutate({
      matchId,
      contestantId,
      runIndex: 0,
      lane: null,
      elapsedTimeMs: elapsedMs,
      reactionMs: null,
      laps: null,
      deviceId: 'web',
    });
  };

  return (
    <Stack gap="lg">
      <Link to="/racing" className="gm-text-muted">
        ← All races
      </Link>

      <Stack direction="row" justify="space-between" align="center" wrap>
        <Heading level={1}>{data.title}</Heading>
        <Badge tone={statusTone(data.status)}>{statusLabel(data.status)}</Badge>
      </Stack>

      <Stack direction="row" gap="sm" wrap>
        <Badge tone="neutral">{formatLabel(data.format)}</Badge>
        <Badge tone="neutral">{scoringMethodLabel(data.scoringMethod)}</Badge>
        {data.organizerName != null && <Text muted>Hosted by {data.organizerName}</Text>}
      </Stack>

      {data.canManage && <ManageBar event={data} />}

      <ContestantsSection event={data} />

      {data.bracket != null && (
        <section aria-label="Bracket">
          <Heading level={2}>Bracket</Heading>
          <BracketView
            bracket={data.bracket}
            canRecord={data.canManage && data.status === 'InProgress'}
            onRecord={(matchId, contestantId, elapsedMs) => onRecord(matchId, contestantId, elapsedMs)}
            recording={record.isPending}
          />
        </section>
      )}

      {data.format === 'TimeTrial' && (
        <section aria-label="Standings">
          <StandingsTable standings={data.standings} />
          {data.canManage && data.status === 'InProgress' && (
            <TimeTrialEntry
              contestants={data.contestants}
              recording={record.isPending}
              onRecord={(contestantId, elapsedMs) => onRecord(null, contestantId, elapsedMs)}
            />
          )}
        </section>
      )}

      {record.isError && (
        <p className="gm-field-error" role="alert">
          {record.error.message}
        </p>
      )}
    </Stack>
  );
}

/** The organiser's lifecycle controls, which depend on the event's current state. */
function ManageBar({ event }: { readonly event: RaceEventDetail }): React.JSX.Element {
  const open = useOpenRegistration(event.id);
  const generate = useGenerateBracket(event.id);
  const complete = useCompleteRaceEvent(event.id);
  const cancel = useCancelRaceEvent(event.id);
  const [confirmCancel, setConfirmCancel] = useState(false);

  const version = event.version;
  const busy = open.isPending || generate.isPending || complete.isPending || cancel.isPending;
  const actionError = open.error ?? generate.error ?? complete.error ?? cancel.error;

  return (
    <Card ariaLabel="Manage race">
      <Stack gap="sm">
        <Stack direction="row" gap="sm" wrap>
          {event.status === 'Draft' && (
            <Button variant="primary" disabled={busy} onClick={() => open.mutate({ version })}>
              Open registration
            </Button>
          )}
          {event.status === 'Open' && (
            <Button variant="primary" disabled={busy} onClick={() => generate.mutate({ version })}>
              Generate bracket
            </Button>
          )}
          {event.status === 'InProgress' && (
            <Button variant="primary" disabled={busy} onClick={() => complete.mutate({ version })}>
              Complete event
            </Button>
          )}
          {(event.status === 'Draft' || event.status === 'Open') && (
            <Link className="gm-button gm-button--secondary" to={`/racing/${event.id}/edit`}>
              Edit
            </Link>
          )}
          {event.status !== 'Completed' && event.status !== 'Cancelled' && (
            <Button variant="danger" disabled={busy} onClick={() => setConfirmCancel(true)}>
              Cancel
            </Button>
          )}
        </Stack>
        {actionError != null && (
          <p className="gm-field-error" role="alert">
            {actionError.message}
          </p>
        )}
      </Stack>

      <ConfirmDialog
        open={confirmCancel}
        title="Cancel this race?"
        message="Cancelling stops the event. This cannot be undone."
        confirmLabel="Cancel race"
        destructive
        onConfirm={() => {
          setConfirmCancel(false);
          cancel.mutate({ version });
        }}
        onCancel={() => setConfirmCancel(false)}
      />
    </Card>
  );
}

/** The contestants list, with registration and withdrawal while the field is open. */
function ContestantsSection({ event }: { readonly event: RaceEventDetail }): React.JSX.Element {
  const register = useRegisterContestant(event.id);
  const withdraw = useWithdrawContestant(event.id);
  const [name, setName] = useState('');
  const [dialIn, setDialIn] = useState('');

  const canEditField = event.canManage && (event.status === 'Draft' || event.status === 'Open');
  const usesDialIn = event.scoringMethod === 'DialInHandicap';

  const submit = (): void => {
    register.mutate(
      { displayName: name.trim(), seed: null, dialIn: dialIn.trim() === '' ? null : dialIn.trim(), linkedUserId: null, vehicleId: null },
      {
        onSuccess: () => {
          setName('');
          setDialIn('');
        },
      },
    );
  };

  return (
    <section aria-label="Contestants">
      <Heading level={2}>Contestants ({event.contestants.length})</Heading>
      <Stack gap="sm">
        {event.contestants.length === 0 && <Text muted>No contestants registered yet.</Text>}
        {event.contestants.map((contestant) => (
          <ContestantRow
            key={contestant.id}
            contestant={contestant}
            usesDialIn={usesDialIn}
            canWithdraw={canEditField}
            withdrawing={withdraw.isPending}
            onWithdraw={() => withdraw.mutate({ contestantId: contestant.id, version: contestant.version })}
          />
        ))}

        {canEditField && (
          <Card ariaLabel="Register a contestant">
            <Stack direction="row" gap="sm" align="end" wrap>
              <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} />
              {usesDialIn && (
                <TextField
                  label="Dial-in (s)"
                  type="number"
                  inputMode="decimal"
                  value={dialIn}
                  onChange={(e) => setDialIn(e.target.value)}
                />
              )}
              <Button variant="primary" disabled={name.trim() === '' || register.isPending} onClick={submit}>
                Register
              </Button>
            </Stack>
            {register.isError && (
              <p className="gm-field-error" role="alert">
                {register.error.message}
              </p>
            )}
          </Card>
        )}
      </Stack>
    </section>
  );
}

function ContestantRow(props: {
  readonly contestant: Contestant;
  readonly usesDialIn: boolean;
  readonly canWithdraw: boolean;
  readonly withdrawing: boolean;
  readonly onWithdraw: () => void;
}): React.JSX.Element {
  const { contestant, usesDialIn, canWithdraw, withdrawing, onWithdraw } = props;

  return (
    <Stack direction="row" gap="sm" justify="space-between" align="center" wrap>
      <Stack direction="row" gap="sm" align="center" wrap>
        <Badge tone="neutral">#{contestant.seed}</Badge>
        <Text>{contestant.displayName}</Text>
        {usesDialIn && contestant.dialIn != null && <Text muted size="sm">dial {contestant.dialIn}</Text>}
      </Stack>
      {canWithdraw && (
        <Button variant="danger" disabled={withdrawing} onClick={onWithdraw}>
          Withdraw
        </Button>
      )}
    </Stack>
  );
}

/** Per-contestant time entry for a time trial. */
function TimeTrialEntry(props: {
  readonly contestants: readonly Contestant[];
  readonly recording: boolean;
  readonly onRecord: (contestantId: string, elapsedMs: number) => void;
}): React.JSX.Element {
  const { contestants, recording, onRecord } = props;

  return (
    <Card ariaLabel="Record times">
      <Stack gap="sm">
        <Heading level={3}>Record a run</Heading>
        {contestants.map((contestant) => (
          <TimeTrialRow key={contestant.id} contestant={contestant} recording={recording} onRecord={onRecord} />
        ))}
      </Stack>
    </Card>
  );
}

function TimeTrialRow(props: {
  readonly contestant: Contestant;
  readonly recording: boolean;
  readonly onRecord: (contestantId: string, elapsedMs: number) => void;
}): React.JSX.Element {
  const { contestant, recording, onRecord } = props;
  const [seconds, setSeconds] = useState('');

  const record = (): void => {
    const parsed = Number.parseFloat(seconds);
    if (!Number.isNaN(parsed)) {
      onRecord(contestant.id, Math.round(parsed * 1000));
      setSeconds('');
    }
  };

  return (
    <Stack direction="row" gap="sm" justify="space-between" align="center" wrap>
      <Text>{contestant.displayName}</Text>
      <Stack direction="row" gap="sm" align="center" wrap>
        <input
          className="gm-input gm-input--inline"
          type="number"
          inputMode="decimal"
          step="0.001"
          placeholder="seconds"
          aria-label={`Time for ${contestant.displayName}`}
          value={seconds}
          onChange={(event) => setSeconds(event.target.value)}
        />
        <Button disabled={seconds.trim() === '' || recording} onClick={record}>
          Record
        </Button>
      </Stack>
    </Stack>
  );
}
